'use client'

import { track } from '@amplitude/analytics-browser'
import { Slot } from '@radix-ui/react-slot'

/**
 * Cited mirror of the ratified partner-events contract in
 * `strategy/.docs/analytics/amplitude-events-partners.md` — each interaction type
 * pairs only with the ui_source values the dictionary declares for it. The
 * dictionary is the source of truth; sync when its changelog touches this event.
 */
export type PartnersInteractionProperties =
    | {
          interaction_type: 'get_started_opened'
          ui_source: 'hero_cta' | 'bottom_band_cta' | 'how_it_works_cta'
      }
    | {
          interaction_type: 'how_it_works_opened'
          ui_source: 'bottom_band_link' | 'get_started_form_link'
      }

/**
 * Fires `Partners Page Interacted` for a cross-page affordance on the partner
 * surface. Attaches a click handler to the anchor its caller renders; its
 * property union mirrors the partner event contract. Amplitude-only; `page_type`
 * is stamped by the page-type enrichment plugin. `link_url` is read from the
 * clicked anchor's rendered href so it always matches what the visitor saw
 * (locale-prefixed, trailing slash).
 */
export const TrackPartnerInteraction = ({
    properties,
    children,
}: {
    properties: PartnersInteractionProperties
    /** Exactly one element — the anchor this attaches tracking to. */
    children: React.ReactElement
}): React.ReactElement => {
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        const href = event.currentTarget.getAttribute('href')
        track('Partners Page Interacted', {
            ...properties,
            ...(href ? { link_url: href } : {}),
        })
    }

    return <Slot onClick={handleClick}>{children}</Slot>
}
